import { HttpClient } from '@angular/common/http';
import { Component, ChangeDetectorRef } from '@angular/core';
import * as XLSX from 'xlsx';
import { environment } from '../../../../../environments/environment';
interface ImportResult {
  nom: string;
  status: 'ok' | 'warning' | 'error';
  message?: string;
}

interface ExistingCartouche {
  id: number;
  nom?: string | null;
  utilise?: boolean | null;
  inutilisable?: boolean | null;
}

@Component({
  selector: 'app-import-excel-cartouches',
  templateUrl: './import-excel.page.html',
  styleUrls: ['./import-excel.page.scss']
})
export class ImportExcelCartouchesPage {
  error: string | null = null;
  success: string | null = null;
  file: File | null = null;
  rows: any[] = [];
  results: ImportResult[] = [];
  isLoading = false;
  apiBaseUrl = environment.apiBaseUrl;
  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.file = input.files[0];
      this.error = null;
      this.success = null;
      this.rows = [];
      this.results = [];
    }
  }

  async onImport() {
    if (!this.file) {
      this.error = 'Veuillez sélectionner un fichier.';
      this.cdr.detectChanges();
      return;
    }
    this.isLoading = true;
    this.results = [];
    this.success = null;
    this.error = null;
    this.cdr.detectChanges();
    const reader = new FileReader();
    reader.onload = async (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        if (!json.length) {
          this.error = 'Le fichier est vide.';
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }
        const [header, ...rows] = json;
        // Correction : s'assurer que header est bien un tableau
        if (!Array.isArray(header) || header.length < 3 || header[0] !== 'Nom' || header[1] !== 'Lot' || header[2] !== 'Chimie') {
          this.error = "Les colonnes attendues sont : Nom, Lot, Chimie (dans cet ordre).";
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }
        this.rows = rows.map((row: unknown) => {
          if (Array.isArray(row)) {
            return {
              Nom: row[0],
              Lot: row[1],
              Chimie: row[2]
            };
          } else {
            return { Nom: '', Lot: '', Chimie: '' };
          }
        });

        const seenNames = new Set<string>();
        const existingCartouchesByName = new Map<string, ExistingCartouche>();

        try {
          const existingCartouchesResp = await this.http
            .get<{ items?: ExistingCartouche[] }>(`${this.apiBaseUrl}/api/cartouches`)
            .toPromise();

          for (const item of existingCartouchesResp?.items ?? []) {
            const normalizedExisting = String(item?.nom ?? '').trim().toLowerCase();
            if (normalizedExisting && typeof item?.id === 'number') {
              existingCartouchesByName.set(normalizedExisting, item);
            }
          }
        } catch {
          // Si le preload échoue, on laisse la logique API gérer la création/mise à jour.
        }
        // Appel API pour chaque cartouche avec récupération/création des ids lot et chimie
        let processed = 0;
        for (const cartouche of this.rows) {
          const normalizedNom = String(cartouche.Nom ?? '').trim();
          const dedupeKey = normalizedNom.toLowerCase();

          if (!normalizedNom) {
            this.results.push({ nom: '(vide)', status: 'warning', message: 'Nom vide, ligne ignorée' });
            processed += 1;
            continue;
          }

          if (seenNames.has(dedupeKey)) {
            this.results.push({ nom: normalizedNom, status: 'warning', message: 'Déjà présente dans le fichier, ligne ignorée' });
            processed += 1;
            continue;
          }
          seenNames.add(dedupeKey);

          try {
            // Récupérer/créer l'id du lot (query param)
            const lotIdResp = await this.http.get<{id: number}>(`${this.apiBaseUrl}/api/lots-cartouches/getIdByNom`, { params: { nom: cartouche.Lot } }).toPromise();
            const lot_cartouche_id = lotIdResp?.id;
            // Récupérer/créer l'id de la chimie (query param)
            const chimieIdResp = await this.http.get<{id: number}>(`${this.apiBaseUrl}/api/chimie/getIdByNom`, { params: { nom: cartouche.Chimie } }).toPromise();
            const chimie_id = chimieIdResp?.id;

            const existing = existingCartouchesByName.get(dedupeKey);
            if (existing && typeof existing.id === 'number') {
              await this.http.put(`${this.apiBaseUrl}/api/cartouches/${existing.id}`, {
                nom: String(existing.nom ?? normalizedNom).trim() || normalizedNom,
                lot_cartouche_id,
                chimie_id,
                utilise: existing.utilise ?? false,
                inutilisable: existing.inutilisable ?? false
              }).toPromise();
            } else {
              const created = await this.http.post<ExistingCartouche>(`${this.apiBaseUrl}/api/cartouches`, {
                nom: normalizedNom,
                lot_cartouche_id,
                chimie_id,
                utilise: false,
                inutilisable: false
              }).toPromise();

              if (created && typeof created.id === 'number') {
                existingCartouchesByName.set(dedupeKey, created);
              }
            }

            this.results.push({ nom: normalizedNom, status: 'ok' });
          } catch (err: any) {
            if (err.status === 409) {
              this.results.push({ nom: normalizedNom, status: 'warning', message: 'Déjà existante en base, ligne ignorée' });
            } else {
              const errorMsg = this.getErrorMessage(err);
              this.results.push({ nom: normalizedNom, status: 'error', message: errorMsg });
            }
          }
          processed += 1;
          if (processed % 10 === 0 || processed === this.rows.length) {
            this.cdr.detectChanges();
          }
        }
        const okCount = this.results.filter(r => r.status === 'ok').length;
        const warningCount = this.results.filter(r => r.status === 'warning').length;
        const errorCount = this.results.filter(r => r.status === 'error').length;
        this.success = (okCount > 0 || warningCount > 0)
          ? `Import terminé : ${okCount} cartouche(s) importée(s) ou mise(s) à jour, ${warningCount} ligne(s) ignorée(s).`
          : null;
        this.error = null;
      } catch (e: any) {
        const errorMsg = this.getErrorMessage(e);
        this.error = `Erreur de lecture ou d'import : ${errorMsg}`;
      }
      this.isLoading = false;
      this.cdr.detectChanges();
    };
    reader.onerror = () => {
      this.error = 'Erreur de lecture du fichier.';
      this.isLoading = false;
      this.cdr.detectChanges();
    };
    reader.readAsArrayBuffer(this.file);
  }

  private getErrorMessage(err: any): string {
    // Vérifier si c'est une chaîne
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    // Vérifier les propriétés courantes d'erreur
    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    // Sérialiser l'objet si c'est un objet
    if (err.error && typeof err.error === 'object') {
      try {
        return JSON.stringify(err.error);
      } catch {
        return 'Erreur serveur';
      }
    }

    // Vérifier le status HTTP
    if (err.status === 0) {
      return 'Impossible de contacter le serveur';
    }

    if (err.status && err.statusText) {
      return `${err.status} - ${err.statusText}`;
    }

    return 'Erreur inconnue';
  }
}
